import styles from '../../styles/otp-input-overrides.module.sass'
import classNames from 'classnames'
import OtpInput from 'react-otp-input'
import Loader from '@components/common/loader'
import { getFromLocalStorage } from '@lib/utils/local-storage'
import { MASKED_EMAIL } from '@lib/constants/common'
import { useMemo } from 'react'
import { defineMessage, t, Trans } from '@lingui/macro'
import { LinguiJSMessageDescriptor } from '@lib/types/common'

type Props = {
  phone: string
  isEmailIdentifier?: boolean
  otp: string
  errors: any
  resendOtpLoading: boolean
  handleInputChange: (otp: string) => void
  handlePhoneVerificationFormSubmit: () => void
  timer: number
  timerActive: boolean
  handleResendOtpViaSMS: () => void
  handleSendOtpViaWhatsapp: () => void
  handleSendOtpViaCall: () => void
  handleResendOtpViaEmail: () => void
  isLoaderShown: boolean
  numInputs: number
  sentVia: string
}

export const VerifyPhoneOtp = (props: Props): JSX.Element => {
  const {
    phone,
    isEmailIdentifier = false,
    otp,
    errors,
    resendOtpLoading,
    handleInputChange,
    handleResendOtpViaSMS,
    timerActive,
    handleSendOtpViaWhatsapp,
    handleSendOtpViaCall,
    handleResendOtpViaEmail,
    isLoaderShown,
    numInputs,
    sentVia,
  } = props
  const email = getFromLocalStorage(MASKED_EMAIL)
  const hasEmail = email && email != 'none'
  // List of country codes to which the primary OTP will be sent via whatsapp
  // This is used to show the correct message to the user
  // The same will be handled in the backend
  const whatsappCountryCodes = [
    '92', // Pakistan
    '880', // Bangladesh
    '234', // Nigeria
    '233', // Ghana
    '94', // Sri Lanka
    '255', // Tanzania
    '260', // Zambia
    '62', // Indonesia
    '216', // Tunisia
    '95', // Myanmar
    '975', // Bhutan
    '254', // Kenya
    '977', // Nepal
    '251', // Ethiopia
  ]
  const isSentOtpViaWhatsapp =
    !isEmailIdentifier &&
    phone &&
    whatsappCountryCodes.some((code) => phone.startsWith(code))
  const isIndianUser = !isEmailIdentifier && phone && phone.startsWith('91')

  const getMessage = () => {
    let message: LinguiJSMessageDescriptor = null
    if (isEmailIdentifier) {
      return defineMessage({
        message: `We have sent an email with OTP to ${phone}`,
      })
    }
    if (hasEmail) {
      if (sentVia == 'both') {
        if (isSentOtpViaWhatsapp) {
          message = defineMessage({
            message: `OTP has been sent to your whatsapp and associated email address ${email}`,
          })
        } else {
          message = defineMessage({
            message: `OTP has been sent to your phone and associated email address ${email}`,
          })
        }
      } else {
        message = defineMessage({
          id: `We have sent {sentVia} with OTP to {phone}`,
          message: `We have sent ${sentVia} with OTP to +${phone}`,
        })
      }
    } else {
      if (sentVia == 'both') {
        message = defineMessage({
          message: `We have sent an SMS with OTP to +${phone}`,
        })
      } else {
        message = defineMessage({
          id: 'We have sent {sentVia} with OTP to {phone}',
          message: `We have sent ${sentVia} with OTP to +${phone}`,
        })
      }
    }

    return message
  }

  const message = useMemo(() => getMessage(), [
    sentVia,
    hasEmail,
    email,
    phone,
    isEmailIdentifier,
  ])

  const resendOtpBtnClass = classNames({
    'text-base leading-tight text-grey-800 hover:text-grey-900 cursor-pointer select-none': true,
    'hover:opacity-50 opacity-50 cursor-not-allowed': resendOtpLoading,
    'cursor-not-allowed': timerActive,
  })

  const resendBtnClass =
    'border rounded-md border-orange px-2 py-1 hover:bg-orange hover:text-white'

  return (
    <div data-testid="verify-otp">
      <div className="text-grey text-4xl lg:text-5xl font-600 leading-tight mb-3 pt-5">
        <p>
          <Trans>Login with OTP</Trans>
        </p>
      </div>
      <div className="text-grey text-lg font-600 leading-tight my-2">
        <Trans id={message.id} values={message.values} />
      </div>
      <div className="mt-10 max-w-sm">
        <OtpInput
          value={otp}
          inputType="tel"
          shouldAutoFocus={true}
          renderSeparator={<span className="hidden lg:flex">-</span>}
          onChange={handleInputChange}
          numInputs={numInputs}
          renderInput={(inputProps) => (
            <div className={`${styles['otp-input']} lg:mr-10`}>
              <input {...inputProps} type="text" autoComplete="one-time-code" />
            </div>
          )}
        />
        <div className="h-2">
          {isLoaderShown && <Loader classes="h-6 w-6 mx-auto my-4" />}
        </div>
        <div className="mt-6">
          {errors.token &&
            errors.token.map((val, idx) => (
              <div
                className="text-red-200 text-sm leading-4 font-400"
                key={idx}
              >
                {val}
              </div>
            ))}
          {errors.nonFieldErrors &&
            errors.nonFieldErrors.map((val, idx) => (
              <div
                className="text-red-200 text-sm leading-4 font-400"
                key={idx}
              >
                {typeof val === 'string' ? val : <Trans id={val.id} />}
              </div>
            ))}
        </div>
      </div>
      <div className={resendOtpBtnClass}>
        {timerActive ? (
          t`In case of delay, please wait for some time before requesting for the OTP again.`
        ) : (
          <span>
            <Trans>Resend OTP on</Trans>{' '}
            {isEmailIdentifier ? (
              <button
                className={resendBtnClass}
                onClick={handleResendOtpViaEmail}
              >
                <Trans id="E-Mail">E-Mail</Trans>
              </button>
            ) : (
              <>
                {isIndianUser ? (
                  ''
                ) : (
                  <>
                    <button
                      className={resendBtnClass}
                      onClick={handleSendOtpViaWhatsapp}
                    >
                      <Trans>WhatsApp</Trans>
                    </button>{' '}
                    <Trans>or</Trans>
                  </>
                )}{' '}
                <button
                  className={resendBtnClass}
                  onClick={handleSendOtpViaCall}
                >
                  <Trans>Call</Trans>
                </button>{' '}
                <Trans>or</Trans>{' '}
                <button
                  className={resendBtnClass}
                  onClick={handleResendOtpViaSMS}
                >
                  <Trans>SMS</Trans>
                </button>{' '}
                {hasEmail ? (
                  <>
                    <Trans>or</Trans>{' '}
                    <button
                      className={resendBtnClass}
                      onClick={handleResendOtpViaEmail}
                    >
                      <Trans id="E-Mail">E-Mail</Trans>
                    </button>
                  </>
                ) : (
                  ''
                )}
              </>
            )}
          </span>
        )}
      </div>
    </div>
  )
}

export default VerifyPhoneOtp
